import { useAtomValue } from "jotai";
import first from "lodash/first";
import { type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useTrackImpressionEventByIntersection } from "shared/hooks";

import { useArticleDateUtils } from "scmp-app/components/article/article-date/hooks";
import { usePlusArticlePopup } from "scmp-app/components/plus/article-popup/hooks";
import { plusPopupAtom } from "scmp-app/components/plus/atoms";
import { usePlusDetailedListingPopup } from "scmp-app/components/plus/detailed-listing-popup/hooks";
import { PlusPaywallSuspense } from "scmp-app/components/plus/plus-paywall-suspense";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { dailyPulseBlockQuery$key } from "scmp-app/queries/__generated__/dailyPulseBlockQuery.graphql";

import IconArrow from "./icon-arrow.svg";
import { Link } from "./link";
import {
  Container,
  Content,
  ContentContainer,
  ContentOverlayer,
  Footer,
  Header,
  Heading,
  PlaceHolderContainer,
  PreviousEditions,
  PublishedContainer,
  PublishedDate,
  ReadFullDailyPulseButton,
  StyledContentBody,
  StyledContentHeadline,
  StyledImagePlaceholder,
} from "./styles";

export type Props = {
  className?: string;
  reference: dailyPulseBlockQuery$key;
};

export const DailyPulseBlock: FunctionComponent<Props> = ({ className, reference }) => {
  const content = useFragment(
    graphql`
      fragment dailyPulseBlockQuery on Query
      @argumentDefinitions(dailyPulseTypeUuid: { type: "String!" }) {
        dailyPulseQuery: articles(
          filter: { typeIds: [$dailyPulseTypeUuid] }
          first: 1
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) {
          edges {
            node {
              ...contentBodyContent
              ...contentHeadlineContent
              ...hooksArticleDateUtilsContent
              ...hooksPlusArticleLinkHandlerArticle
              entityId
              urlAlias
            }
          }
        }
      }
    `,
    reference,
  );

  const topArticle = first(content.dailyPulseQuery.edges)?.node;
  const { publishedDate } = useArticleDateUtils(topArticle, "h:mma, d MMM yyyy");
  const { handleOpen: handleOpenDetailedListingPopup } = usePlusDetailedListingPopup();
  const { handleOpen: handleOpenArticlePopup } = usePlusArticlePopup();

  const { isShowArticlePopup: shouldDisableImpressionTracking } = useAtomValue(plusPopupAtom);

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: () => ({
        action: "imp",
        category: "recirculation",
        customized_parameters: {
          page_type: "homepage",
          product: "plus",
          widget_name: "daily_pulse",
        },
        subcategory: "widget",
      }),
      options: {
        isSendGA4Tracking: true,
        shouldDisable: shouldDisableImpressionTracking,
        shouldSendOnce: true,
      },
    });

  const handleReadFullDailyPulseButtonClick = () => {
    if (!topArticle) {
      return;
    }

    sendGA4Tracking({
      action: "click",
      category: "recirculation",
      customized_parameters: {
        article_id: topArticle.entityId,
        destination_url: topArticle.urlAlias,
        page_type: "homepage",
        product: "plus",
        widget_name: "daily_pulse",
      },
      subcategory: "widget",
    });

    handleOpenArticlePopup(topArticle);
  };

  if (!topArticle) return null;

  return (
    <Container className={className}>
      <PlusPaywallSuspense
        fallback={
          <PlaceHolderContainer>
            <StyledImagePlaceholder />
          </PlaceHolderContainer>
        }
      >
        <Header>
          <Heading>DAILY PULSE</Heading>
          <StyledContentHeadline preferSocialHeadline reference={topArticle} />
          <PublishedContainer>
            Published: <PublishedDate>{publishedDate}</PublishedDate>
          </PublishedContainer>
        </Header>
        <ContentContainer>
          <Content>
            <StyledContentBody
              extraComponentMap={{
                a: Link,
                iframe: null,
                img: null,
              }}
              reference={topArticle}
            />
            <ContentOverlayer />
          </Content>
          <Footer>
            <ReadFullDailyPulseButton
              noStyle
              onClick={handleReadFullDailyPulseButtonClick}
              ref={captureTrackImpressionEventTargetElement}
            >
              READ FULL DAILY PULSE
            </ReadFullDailyPulseButton>
            <PreviousEditions
              onClick={() =>
                handleOpenDetailedListingPopup({
                  pgtype: "homepage",
                  type: "daily-pulse",
                })
              }
            >
              <IconArrow />
              Previous editions
            </PreviousEditions>
          </Footer>
        </ContentContainer>
      </PlusPaywallSuspense>
    </Container>
  );
};

DailyPulseBlock.displayName = "DailyPulseBlock";
